import type { ISimpleEntity } from '@manager/api/types/general-types';
import { useTranslate } from '@manager/composables/use-translate';
import { useUser } from '@manager/composables/use-user';
import { getPaymentMethodName } from '@manager/utils/payment-method-utils';
import dayjs, { type Dayjs } from 'dayjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { isNumber } from 'lodash-es';
import { computed } from 'vue';

export const DATE_FORMAT_ISO_DATE = 'YYYY-MM-DD';

export function formatNumbersWithFloatingDot(
  origin: number,
  decimalPoints = 2
): number {
  return Number(Number.parseFloat(origin.toString()).toFixed(decimalPoints));
}

export function formatIdString(id: number | null | undefined): string {
  const arr = [];
  if (id == null) {
    return '';
  }
  if (!Number(id)) {
    return id.toString();
  }
  const strId = id.toString();

  for (let i = 0; i < strId.length; i += 4) {
    arr.push(strId.substring(i, i + 4));
  }

  return arr.join('-');
}

export function formatDefaultPaymentMethods(methods?: ISimpleEntity[] | null) {
  return methods?.map((method) => ({
    id: method.id,
    methodId: method.name,
    name: getPaymentMethodName(method.name),
  }));
}

/**
 * Returns the current currency symbol based on the users locale
 * @param locale de | en | sv
 * @param currency the currency symbol
 */
export function getCurrencySymbol(locale: string, currency: string) {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol',
  });

  return formatter.format(0).replace(/\d/g, '').trim();
}

export function useCurrency() {
  const { locale } = useTranslate();
  const { currency: userCurrency } = useUser();
  const currency = computed(() => userCurrency.value || 'EUR');

  return {
    symbol: computed(() => getCurrencySymbol(locale.value, currency.value)),
    currency,
  };
}

/**
 * @deprecated use `useFormat()` -> `parseStringToNumber`
 * Parses a number string in the english format "nnn.dd" (e.g. 100.12) to numbers.
 * Be aware that it does not handle german format like "100,12".
 */
export function parseToNumber(
  numberString: string | number,
  fallback = 0
): number {
  if (isNumber(numberString)) {
    return numberString;
  }

  if (numberString == null) {
    return fallback;
  }

  const parsed = parseFloat(numberString.replaceAll(',', '').trim());

  if (isNaN(parsed)) {
    return fallback;
  }

  return parsed;
}

export function formatISODate(
  date: string | Date | Dayjs | null | undefined
): string {
  return date && dayjs(date).isValid()
    ? dayjs(date).format(DATE_FORMAT_ISO_DATE)
    : '';
}

export function formatPhoneNumber(
  value:
    | string
    | null
    | undefined
    | {
        phone_prefix?: string | null;
        phone_number?: string | null;
      }
): string {
  if (!value) {
    return '';
  }

  const raw =
    typeof value === 'string'
      ? value
      : [value.phone_prefix, value.phone_number]
          .filter((part) => !!part?.trim())
          .join(' ');

  if (!raw.trim()) {
    return '';
  }

  const parsed = parsePhoneNumberFromString(raw);
  return parsed?.formatInternational() ?? raw;
}

function toLocale(locale: string): string {
  return locale === 'en' ? 'en-gb' : locale;
}

export function formatDate(
  dateString: string | Date | Dayjs | null | undefined,
  locale: string
): string | undefined {
  if (dateString == null) {
    return;
  }

  return dayjs(dateString).isValid()
    ? dayjs(dateString).locale(toLocale(locale)).format('ll')
    : undefined;
}

export function formatTimeZoneDateTime(
  date: Date | Dayjs | string = new Date(),
  locale: string,
  timezone: string
): string {
  const format = 'll, LT';

  if (dayjs.isDayjs(date)) {
    return date.locale(toLocale(locale)).tz(timezone).format(format);
  } else {
    return dayjs.utc(date).locale(toLocale(locale)).tz(timezone).format(format);
  }
}

export function formatTimeZoneDate(
  date: Date | Dayjs | string = new Date(),
  locale: string,
  timezone: string
): string {
  const format = 'll';

  if (dayjs.isDayjs(date)) {
    return date.locale(toLocale(locale)).tz(timezone).format(format);
  } else {
    return dayjs.utc(date).locale(toLocale(locale)).tz(timezone).format(format);
  }
}

export function formatTimeZoneTime(
  date: Date | Dayjs | string = new Date(),
  locale: string,
  timezone: string
): string {
  const format = 'HH:mm';

  if (dayjs.isDayjs(date)) {
    return date.locale(toLocale(locale)).tz(timezone).format(format);
  } else {
    return dayjs.utc(date).locale(toLocale(locale)).tz(timezone).format(format);
  }
}
